import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaUser,
  FaLock,
  FaSpinner,
  FaEye,
  FaEyeSlash,
  FaGoogle,
  FaArrowLeft,
  FaWhatsapp,
  FaSms,
  FaEdit,
} from "react-icons/fa";
import {
  login,
  requestOtp,
  verifyOtp,
  reset,
} from "../features/auth/authSlice";
import { api } from "../utils/apiUtils";
import { getFcmToken } from "../firebase/getFcmToken";

const Login = () => {
  // Main login method: "password" or "otp"
  const [loginMethod, setLoginMethod] = useState("otp");
  // OTP verification step: "input" or "verify"
  const [currentStep, setCurrentStep] = useState("input");
  // OTP role: "user" or "corporate_staff"
  const [otpRole, setOtpRole] = useState("user");
  const [corporateOtpLoading, setCorporateOtpLoading] = useState(false);
  const [fieldError, setFieldError] = useState(null);

  // Password login form data
  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });

  // OTP login form data
  const [otpData, setOtpData] = useState({
    phone: "",
    otp: ["", "", "", ""],
  });
  const [timeLeft, setTimeLeft] = useState(120);
  const [canResend, setCanResend] = useState(false);
  const otpInputs = useRef([]);

  const [showPassword, setShowPassword] = useState(false);

  const { identifier, password } = formData;
  const { phone, otp } = otpData;

  // Timer countdown effect for OTP resend
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { user, isLoading, isError, isSuccess, message } = useSelector(
    (state) => state.auth
  );

  // Get redirect URL from query params or location state
  const queryParams = new URLSearchParams(location.search);
  const redirectUrl = queryParams.get("redirect");
  const fromLocation = location.state?.from?.pathname + location.state?.from?.search;

  // Debug logging for redirect URL handling
  console.log("🔍 Login - Current location:", location);
  console.log("🔍 Login - Raw search params:", location.search);
  console.log("🔍 Login - All query params:", Object.fromEntries(queryParams.entries()));
  console.log("🔍 Login - Raw redirect param:", redirectUrl);
  console.log("🔍 Login - From location (PrivateRoute):", fromLocation);

  useEffect(() => {
    if (isError && message) {
      toast.error(message);
      dispatch(reset());
    }

    if (isSuccess && user) {
      console.log("✅ Login successful, processing redirect...");
      console.log("🔄 Redirect URL from query params:", redirectUrl);

      dispatch(reset());

      // Check for booking intent stored during the booking process
      const bookingIntent = JSON.parse(
        localStorage.getItem("bookingIntent") || "null",
      );

      const storedCab = JSON.parse(
        localStorage.getItem("selectedCabBeforeLogin") || "null",
      );

      if (redirectUrl) {
        console.log("🎯 Using redirect URL from query params");
        console.log("🔍 Raw redirect URL:", redirectUrl);

        const decodedUrl = decodeURIComponent(redirectUrl);
        console.log("🔍 Decoded URL:", decodedUrl);

        try {
          const url = new URL(decodedUrl, window.location.origin);
          const pathWithSearch = url.pathname + url.search;
          console.log("🔍 Parsed URL components:");
          console.log("  - pathname:", url.pathname);
          console.log("  - search:", url.search);
          console.log("  - href:", url.href);
          console.log("🔄 Final navigation path:", pathWithSearch);
          navigate(pathWithSearch, { replace: true });
        } catch (urlError) {
          console.error("❌ URL parsing error:", urlError);
          console.log("🔄 Falling back to direct navigation:", decodedUrl);
          navigate(decodedUrl, { replace: true });
        }
      } else if (fromLocation && fromLocation !== 'undefined' && fromLocation !== '/') {
        console.log("🎯 Using from location (PrivateRoute redirect):", fromLocation);
        navigate(fromLocation, { replace: true });
      } else if (bookingIntent && Date.now() - new Date(bookingIntent.timestamp).getTime() < 900000) {
        console.log("📋 Using booking intent data:", bookingIntent);
        localStorage.removeItem("bookingIntent");

        const params = new URLSearchParams();
        params.append("from", bookingIntent.from);
        params.append("to", bookingIntent.to);
        params.append("cabId", bookingIntent.cabId);
        params.append("cabName", bookingIntent.cabName);
        params.append("tripType", bookingIntent.type);
        params.append("amount", bookingIntent.amount);
        params.append("distance", bookingIntent.distance);
        params.append("date", bookingIntent.date);
        params.append("time", bookingIntent.time);

        if (bookingIntent.serviceType === "rental" && bookingIntent.packageName) {
          params.append("service", "rental");
          params.append("package", bookingIntent.packageName);
        } else if (bookingIntent.serviceType === "airport") {
          params.append("service", "airport");
          if (bookingIntent.airportCode) params.append("airport", bookingIntent.airportCode);
          if (bookingIntent.transferDirection) params.append("direction", bookingIntent.transferDirection);
        }

        if (bookingIntent.viaCities && bookingIntent.viaCities.length > 0) {
          const viaStr = Array.isArray(bookingIntent.viaCities)
            ? bookingIntent.viaCities.join("|")
            : bookingIntent.viaCities;
          params.append("via", viaStr);
        }

        if (bookingIntent.returnDate) params.append("returnDate", bookingIntent.returnDate);
        if (bookingIntent.returnTime) params.append("returnTime", bookingIntent.returnTime);

        const bookingUrl = `/booking/customer-details?${params.toString()}`;
        console.log("📍 Navigating to booking page:", bookingUrl);
        navigate(bookingUrl);
      } else if (storedCab && Date.now() - storedCab.timestamp < 300000) {
        console.log("🗃️ Using legacy stored cab data:", storedCab);
        localStorage.removeItem("selectedCabBeforeLogin");

        const searchParams = new URLSearchParams();
        searchParams.append("cabId", storedCab.id);
        searchParams.append("cabName", storedCab.name);
        searchParams.append("from", storedCab.fromCity);
        searchParams.append("to", storedCab.toCity);
        searchParams.append("fromLocation", storedCab.fromLocation);
        searchParams.append("toLocation", storedCab.toLocation);
        searchParams.append("distance", storedCab.distance);
        searchParams.append("date", storedCab.date);
        searchParams.append("time", storedCab.time);
        const fallbackUrl = `/booking/customer-details?${searchParams.toString()}`;
        console.log("📍 Navigating to fallback URL:", fallbackUrl);
        navigate(fallbackUrl);
      } else {
        console.log("No redirect data found, going to home page");
        navigate("/");
      }
    }
  }, [user, isError, isSuccess, message, navigate, dispatch, redirectUrl, fromLocation]);

  useEffect(() => {
    dispatch(reset());
  }, [dispatch]);

  const onChange = (e) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const onOtpChange = (e) => {
    setOtpData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
    // Clear field error when user modifies the phone field
    if (e.target.name === "phone") {
      setFieldError(null);
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otpData.otp];
    newOtp[index] = value;
    setOtpData((prev) => ({ ...prev, otp: newOtp }));
    if (value && index < 3) {
      otpInputs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpData.otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleVerifyOtp(e);
    }
  };

  // Format time remaining for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const onPasswordSubmit = async (e) => {
    e.preventDefault();

    // Validation: Check if both fields are provided
    if (!identifier || !password) {
      toast.error("Please enter both email/phone and password");
      return;
    }

    const isEmail = identifier.includes("@");
    const isPhone = /^[0-9]{10}$/.test(identifier);

    // Validation: Check if identifier is valid email or phone
    if (!isEmail && !isPhone) {
      toast.error(
        "Please enter a valid email address or 10-digit phone number",
      );
      return;
    }

    // Attempt to get FCM token (graceful fallback if not configured)
    let fcmToken = null;
    try {
      fcmToken = await getFcmToken();
    } catch (tokenErr) {
      console.warn('Failed to get FCM token for password login:', tokenErr);
    }

    const userData = isEmail
      ? { email: identifier, password, fcmToken }
      : { phone: identifier, password, fcmToken };

    dispatch(login(userData));
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();

    // Validation: Check if phone is provided
    if (!phone) {
      toast.error("Please enter your phone number");
      return;
    }

    // Validation: Check if phone is valid 10-digit number
    if (!/^[0-9]{10}$/.test(phone)) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    if (otpRole === "corporate_staff") {
      setCorporateOtpLoading(true);
      setFieldError(null);
      try {
        // Use unified API endpoint with role parameter
        const res = await api.post("/auth/request-otp", { phone, role: "staff" });
        if (res?.data?.success) {
          // Store role in sessionStorage for the verification step
          sessionStorage.setItem("loginPhone", phone);
          sessionStorage.setItem("loginAccountType", "corporate_staff");
          setCurrentStep("verify");
          setTimeLeft(120);
          setCanResend(false);
        } else {
          // Handle specific error codes
          const errorCode = res?.data?.code;
          const errorField = res?.data?.field;
          const errorMessage = res?.data?.message || "Failed to send OTP";

          setFieldError(errorField || null);

          if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
            toast.error(`${errorMessage}. Please contact support for assistance.`);
          } else {
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        const errorCode = error?.response?.data?.code;
        const errorField = error?.response?.data?.field;
        const errorMessage = error?.response?.data?.message || error.message || "Network error. Please try again.";

        setFieldError(errorField || null);

        if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
          toast.error(`${errorMessage}. Please contact support for assistance.`);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setCorporateOtpLoading(false);
      }
      return;
    }

    try {
      const result = await dispatch(requestOtp({ phone }));

      if (requestOtp.fulfilled.match(result)) {
        setCurrentStep("verify");
        setTimeLeft(120);
        setCanResend(false);
      } else {
        // Check if error is due to phone being registered as staff
        const errorPayload = result.payload || {};
        const errorCode = typeof errorPayload === "object" ? errorPayload.code : null;
        const errorMessage = typeof errorPayload === "object" ? errorPayload.message : errorPayload;

        if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
          toast.error("This phone number is registered as a Corporate Staff account. Please login using the Corporate Account tab.");
          // Auto-switch to corporate staff tab to help user
          setOtpRole("corporate_staff");
          setCurrentStep("input");
          setOtpData({ phone, otp: ["", "", "", ""] });
        } else {
          toast.error(errorMessage || "Failed to send OTP");
        }
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      console.error("OTP request error:", error);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");

    // Validation: Check if OTP is provided
    if (!otpString) {
      toast.error("Please enter the OTP");
      return;
    }

    // Validation: Check if OTP is valid 4-digit number
    if (!/^[0-9]{4}$/.test(otpString)) {
      toast.error("Please enter a valid 4-digit OTP");
      return;
    }

    if (otpRole === "corporate_staff") {
      setCorporateOtpLoading(true);
      setFieldError(null);
      try {
        // Use unified API endpoint with role parameter
        const res = await api.post("/auth/verify-otp", { phone, otp: otpString, role: "staff" });
        if (res?.data?.success) {
          // Store corporate token for corporate API calls
          const corporateToken = res?.data?.corporateToken || res?.data?.token;
          if (corporateToken) {
            localStorage.setItem("corporateToken", corporateToken);
          }

          // Also store regular token if available for /api/auth/me
          const regularToken = res?.data?.token;
          if (regularToken && regularToken !== corporateToken) {
            localStorage.setItem("token", regularToken);
          }

          // Also store corporate-specific data (user object from backend response)
          const member = res?.data?.data || {};
          localStorage.setItem("corporateMember", JSON.stringify(member));

          // IMPORTANT: Clear personal user session for corporate staff
          // Corporate staff should NOT have user in Redux or localStorage
          localStorage.removeItem("user");
          dispatch(reset()); // Clear any existing Redux auth state

          sessionStorage.removeItem("corporateBookingMode");
          window.dispatchEvent(new Event("corporateSessionChanged"));

          // Clear session storage
          sessionStorage.removeItem("loginPhone");
          sessionStorage.removeItem("loginAccountType");

          // Navigate to corporate profile if staff, dashboard if admin
          const destination = member?.role === "admin" ? "/corporate/dashboard" : "/corporate/profile";
          navigate(destination, { replace: true });
        } else {
          // Handle specific error codes
          const errorCode = res?.data?.code;
          const errorField = res?.data?.field;
          const errorMessage = res?.data?.message || "Invalid OTP";

          setFieldError(errorField || null);

          if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
            toast.error(`${errorMessage}. Please contact support for assistance.`);
          } else {
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        const errorCode = error?.response?.data?.code;
        const errorField = error?.response?.data?.field;
        const errorMessage = error?.response?.data?.message || error.message || "Network error. Please try again.";

        setFieldError(errorField || null);

        if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
          toast.error(`${errorMessage}. Please contact support for assistance.`);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setCorporateOtpLoading(false);
      }
      return;
    }

    try {
      let fcmToken = null;
      try {
        fcmToken = await getFcmToken();
      } catch (tokenErr) {
        console.warn('Failed to get FCM token for OTP login:', tokenErr);
      }
      const result = await dispatch(verifyOtp({ phone, otp: otpString, fcmToken }));

      if (verifyOtp.fulfilled.match(result)) {
        // Successful login
      } else {
        // Check if error is due to phone being registered as staff
        const errorPayload = result.payload || {};
        const errorCode = typeof errorPayload === "object" ? errorPayload.code : null;
        const errorMessage = typeof errorPayload === "object" ? errorPayload.message : errorPayload;

        if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
          toast.error("This phone number is registered as a Corporate Staff account. Please login using the Corporate Account tab.");
          // Auto-switch to corporate staff tab to help user
          setOtpRole("corporate_staff");
          setCurrentStep("input");
          setOtpData({ phone, otp: ["", "", "", ""] });
        } else {
          toast.error(errorMessage || "Invalid OTP");
        }
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      console.error("OTP verification error:", error);
    }
  };

  const resetToPhoneInput = () => {
    setCurrentStep("input");
    setOtpData({ phone, otp: ["", "", "", ""] });
  };

  const switchToPasswordLogin = () => {
    setLoginMethod("password");
    setCurrentStep("input");
    setOtpData({ phone: "", otp: ["", "", "", ""] });
  };

  const switchToOtpLogin = () => {
    setLoginMethod("otp");
    setCurrentStep("input");
    setOtpRole("user");
    setFormData({ identifier: "", password: "" });
  };

  return (
    <div className="auth-background min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="auth-card w-full max-w-96 bg-white rounded-xl shadow-lg p-6">
        {/* Password Login Form - Hidden */}
        {loginMethod === "password" && false && (
          <form onSubmit={onPasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-2">
                Email or Phone
              </label>
              <div className="relative">
                <div className="absolute left-3 top-3 text-gray-400"><FaUser /></div>
                <input
                  type="text"
                  id="identifier"
                  name="identifier"
                  value={identifier}
                  onChange={onChange}
                  className="input-field w-full pl-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                  placeholder="Enter your email or phone number"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-3 text-gray-400"><FaLock /></div>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={password}
                  onChange={onChange}
                  className="input-field w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              <div className="mt-2 text-right">
                <Link
                  to="/user/forgot-password/send-otp"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              className={`primary-btn w-full py-3 rounded-lg transition flex items-center justify-center gap-2 ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Loading...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        )}

        {/* OTP Login Form */}
        {loginMethod === "otp" && (
          <>
            {/* Account Type Selection */}
            <div className="account-type-tabs mb-4">
              <div className="flex bg-gray-200 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => {
                    setOtpRole("user");
                    setCurrentStep("input");
                    setOtpData({ phone: "", otp: ["", "", "", ""] });
                    setFieldError(null);
                  }}
                  className={`flex-1 py-2 px-4 rounded-full text-sm font-semibold transition ${
                    otpRole === "user"
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 bg-transparent"
                  }`}
                >
                  Personal Account
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOtpRole("corporate_staff");
                    setCurrentStep("input");
                    setOtpData({ phone: "", otp: ["", "", "", ""] });
                    setFieldError(null);
                  }}
                  className={`flex-1 py-2 px-4 rounded-full text-sm font-semibold transition ${
                    otpRole === "corporate_staff"
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 bg-transparent"
                  }`}
                >
                  Corporate Account
                </button>
              </div>
              {otpRole === "corporate_staff" && (
                <p className="text-xs text-gray-600 mt-2">Login as corporate staff or admin using phone number</p>
              )}
            </div>

            {/* OTP Input Step */}
            {currentStep === "input" && (
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <div className={`phone-input-wrapper flex items-center border rounded-lg overflow-hidden ${fieldError === "phone" ? "border-red-500 bg-red-50" : "border-gray-300"}`}>
                    <div className="country-code flex items-center gap-2 px-3 py-3 border-r border-gray-300 bg-gray-50">
                      <svg className="flag-icon w-6 h-6" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
                        <rect width="900" height="200" fill="#FF9933"/>
                        <rect y="200" width="900" height="200" fill="#FFFFFF"/>
                        <rect y="400" width="900" height="200" fill="#138808"/>
                        <circle cx="450" cy="300" r="60" fill="#1F41B6"/>
                        <circle cx="450" cy="300" r="55" fill="none" stroke="#1F41B6" strokeWidth="2"/>
                        <line x1="450" y1="245" x2="450" y2="355" stroke="#1F41B6" strokeWidth="1"/>
                        <line x1="395" y1="300" x2="505" y2="300" stroke="#1F41B6" strokeWidth="1"/>
                        <line x1="405" y1="255" x2="495" y2="345" stroke="#1F41B6" strokeWidth="1"/>
                        <line x1="405" y1="345" x2="495" y2="255" stroke="#1F41B6" strokeWidth="1"/>
                        <line x1="415" y1="270" x2="485" y2="330" stroke="#1F41B6" strokeWidth="1"/>
                        <line x1="415" y1="330" x2="485" y2="270" stroke="#1F41B6" strokeWidth="1"/>
                      </svg>
                      <span className="country-dial text-sm">+91</span>
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={phone}
                      onChange={onOtpChange}
                      className="phone-input flex-1 px-4 py-3 outline-none text-sm placeholder-gray-400"
                      placeholder="Enter Mobile Number"
                      maxLength="10"
                      required
                    />
                  </div>
                  {fieldError === "phone" && (
                    <p className="text-red-600 text-xs mt-2">This phone number is already registered as staff. Please contact support.</p>
                  )}
                </div>

                <button
                  type="submit"
                  className={`primary-btn w-full py-3 rounded-full font-semibold transition flex items-center justify-center gap-2 ${
                    (isLoading || corporateOtpLoading)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  disabled={isLoading || corporateOtpLoading}
                >
                  {(isLoading || corporateOtpLoading) ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    'CONTINUE'
                  )}
                </button>
              </form>
            )}

            {/* OTP Verification Step */}
            {currentStep === "verify" && (
              <div>
                {/* Title */}
                <h2 className="text-2xl font-bold mb-1">Verify OTP</h2>

                {/* Subtitle with phone number */}
                <p className="text-gray-600 flex items-center gap-3 mb-3 text-sm">
                  Enter 4 digit OTP sent
                  <button
                    type="button"
                    onClick={resetToPhoneInput}
                    className="flex items-center gap-1 hover:opacity-70 transition cursor-pointer"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
                      <rect width="900" height="200" fill="#FF9933"/>
                      <rect y="200" width="900" height="200" fill="#FFFFFF"/>
                      <rect y="400" width="900" height="200" fill="#138808"/>
                      <circle cx="450" cy="300" r="60" fill="#1F41B6"/>
                      <circle cx="450" cy="300" r="55" fill="none" stroke="#1F41B6" strokeWidth="2"/>
                      <line x1="450" y1="245" x2="450" y2="355" stroke="#1F41B6" strokeWidth="1"/>
                      <line x1="395" y1="300" x2="505" y2="300" stroke="#1F41B6" strokeWidth="1"/>
                      <line x1="405" y1="255" x2="495" y2="345" stroke="#1F41B6" strokeWidth="1"/>
                      <line x1="405" y1="345" x2="495" y2="255" stroke="#1F41B6" strokeWidth="1"/>
                      <line x1="415" y1="270" x2="485" y2="330" stroke="#1F41B6" strokeWidth="1"/>
                      <line x1="415" y1="330" x2="485" y2="270" stroke="#1F41B6" strokeWidth="1"/>
                    </svg>
                    <span>+91 {phone}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" /><path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415" /><path d="M16 5l3 3" /></svg>
                  </button>
                </p>

                {/* OTP Input Fields */}
                <div className="flex justify-center mb-3 gap-4">
                  {otpData.otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (otpInputs.current[index] = el)}
                      type="text"
                      value={digit}
                      maxLength="1"
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      className="w-12 h-10 border-2 border-blue-500 rounded-lg text-center text-lg font-semibold outline-none focus:ring-2 focus:ring-blue-300 transition"
                      inputMode="numeric"
                      autoComplete="off"
                      disabled={isLoading || corporateOtpLoading}
                    />
                  ))}
                </div>

                {/* Resend Timer */}
                <p className="text-gray-700 mb-2 text-sm">
                  Didn't get OTP? Resend OTP in <span className="font-bold">{formatTime(timeLeft)}</span>
                </p>

                {/* Resend Method Buttons */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={handleRequestOtp}
                    className="flex-1 border border-gray-300 rounded-lg py-1.5 px-2 flex items-center justify-center gap-1 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    disabled={!canResend || isLoading || corporateOtpLoading}
                  >
                    {isLoading ? (
                      <FaSpinner className="text-green-500 animate-spin text-sm" />
                    ) : (
                      <FaWhatsapp className="text-green-500" />
                    )}
                    <span className="font-medium">WhatsApp</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleRequestOtp}
                    className="flex-1 border border-gray-300 rounded-lg py-1.5 px-2 flex items-center justify-center gap-1 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    disabled={!canResend || isLoading || corporateOtpLoading}
                  >
                    {isLoading ? (
                      <FaSpinner className="text-gray-500 animate-spin text-sm" />
                    ) : (
                      <FaSms className="text-gray-500" />
                    )}
                    <span className="font-medium">SMS</span>
                  </button>
                </div>

                {/* Submit Button */}
                <form onSubmit={handleVerifyOtp}>
                  <button
                    type="submit"
                    disabled={isLoading || corporateOtpLoading}
                    className={`w-full py-2 rounded-lg font-semibold mb-3 transition flex items-center justify-center gap-2 ${
                      isLoading || corporateOtpLoading
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                  >
                    {isLoading || corporateOtpLoading ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "SUBMIT"
                    )}
                  </button>
                </form>

                {/* Footer Text */}
                <p className="text-xs text-gray-500 text-center mt-2">
                  I agree to receive critical messages such as OTP,
                  booking details on WhatsApp.
                </p>

                {/* Corporate Admin Email Login Link */}
                {otpRole === "corporate_staff" && currentStep === "verify" && (
                  <p className="text-xs text-center text-gray-600 mt-3">
                    Corporate admin?{" "}
                    <Link
                      to="/corporate/login"
                      className="text-blue-600 hover:underline font-semibold"
                    >
                      Login with email instead
                    </Link>
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {currentStep === "input" && (
          <>
            {/* Divider */}
            <div className="divider-section flex items-center gap-3 my-5">
              <div className="divider-line flex-1 h-px bg-gray-300"></div>
              <span className="divider-text text-xs text-gray-500">
                Or Login/Signup With
              </span>
              <div className="divider-line flex-1 h-px bg-gray-300"></div>
            </div>

            {/* Social Login Buttons */}
            <div className="social-buttons flex justify-center gap-6 mb-6">
              <button
                type="button"
                className="social-btn w-10 h-10 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-50 transition"
                aria-label="Login with Google"
              >
                <FaGoogle className="text-lg" />
              </button>
            </div>

            {/* Illustration */}
            

            {/* Footer */}
            <p className="footer-text text-xs text-center text-gray-500">
              By Proceeding, you agree to <span className="font-semibold text-blue-600">Cabzigo</span>Trip's{" "}
              <span className="font-semibold text-blue-600">Privacy Policy</span>,{" "}
              <span className="font-semibold text-blue-600">User Agreement</span> and{" "}
              <span className="font-semibold text-blue-600">T&Cs</span>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
